import React, { useState } from 'react';
import { useAIRouting, useLocalStorage } from '@/hooks/use-api';
import { APIUtils } from '@/utils/api-utils';
import {
  Brain,
  Zap,
  Play,
  Loader2,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Clock,
  DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';

function AIRouting() {
  const [taskType, setTaskType] = useLocalStorage('ai-task-type', 'code-generation');
  const [complexity, setComplexity] = useLocalStorage('ai-complexity', 'medium');
  const [maxResponseTime, setMaxResponseTime] = useLocalStorage('ai-response-time', '5000');
  const [maxCost, setMaxCost] = useLocalStorage('ai-max-cost', '0.10');
  const [minAccuracy, setMinAccuracy] = useLocalStorage('ai-min-accuracy', '0.90');

  const { routing, loading, error, routeModel } = useAIRouting();

  const handleRoute = async () => {
    const request = {
      type: taskType as any,
      complexity: complexity as any,
      requirements: {
        maxResponseTime: parseInt(maxResponseTime),
        maxCost: parseFloat(maxCost),
        minAccuracy: parseFloat(minAccuracy),
      },
    };

    toast.promise(
      routeModel(request),
      {
        loading: 'Finding optimal AI model...',
        success: 'AI model selected successfully',
        error: (err) => err || 'Failed to route AI model',
      }
    );
  };

  const taskTypes = [
    { value: 'code-generation', label: 'Code Generation', description: 'Generate code snippets and functions' },
    { value: 'reasoning', label: 'Reasoning', description: 'Logical problem solving and analysis' },
    { value: 'translation', label: 'Translation', description: 'Code language translation' },
    { value: 'analysis', label: 'Analysis', description: 'Code review and analysis' },
    { value: 'optimization', label: 'Optimization', description: 'Performance optimization' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">AI Model Routing</h1>
        <p className="text-gray-600 mt-2">
          Intelligent AI model selection based on task requirements and performance needs
        </p>
      </div>

      {/* Configuration Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Configuration</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Task Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Type
            </label>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {taskTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {taskTypes.find(t => t.value === taskType)?.description}
            </p>
          </div>

          {/* Complexity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Complexity
            </label>
            <select
              value={complexity}
              onChange={(e) => setComplexity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="low">Low - Simple tasks</option>
              <option value="medium">Medium - Moderate complexity</option>
              <option value="high">High - Complex tasks</option>
            </select>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Response Time (ms)
            </label>
            <input
              type="number"
              value={maxResponseTime}
              onChange={(e) => setMaxResponseTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum acceptable response time</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Cost ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={maxCost}
              onChange={(e) => setMaxCost(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum cost per request</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Accuracy
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={minAccuracy}
              onChange={(e) => setMinAccuracy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum accuracy required (0-1)</p>
          </div>
        </div>

        {/* Route Button */}
        <button
          onClick={handleRoute}
          disabled={loading}
          className="flex items-center justify-center space-x-2 w-full md:w-auto px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-6"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Finding optimal model...</span>
            </>
          ) : (
            <>
              <Brain className="w-5 h-5" />
              <span>Route AI Model</span>
            </>
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Routing Failed</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Routing Results */}
      {routing && (
        <div className="space-y-6">
          {/* Selected Model */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Selected Model</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{routing.selectedModel.name}</h3>
                  <p className="text-gray-600">{routing.selectedModel.provider}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">
                  {APIUtils.formatPercentage(routing.confidence)}
                </div>
                <p className="text-sm text-gray-600">Confidence</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Cost:</span>
                <span className="font-medium">{APIUtils.formatCost(routing.selectedModel.cost)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Performance:</span>
                <span className="font-medium">{APIUtils.formatPercentage(routing.selectedModel.performance)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Response:</span>
                <span className="font-medium">Fast</span>
              </div>
            </div>

            {/* Capabilities */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Capabilities</h4>
              <div className="flex flex-wrap gap-2">
                {routing.selectedModel.capabilities.map((capability: string, index: number) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full"
                  >
                    {capability}
                  </span>
                ))}
              </div>
            </div>

            {/* Reasoning */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Routing Reasoning</h4>
              <p className="text-sm text-gray-600">{routing.reasoning}</p>
            </div>
          </div>

          {/* Alternative Models */}
          {routing.alternatives && routing.alternatives.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Alternative Models</h2>
              <div className="space-y-3">
                {routing.alternatives.map((model: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium">{model.name}</h4>
                      <p className="text-sm text-gray-600">{model.provider}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{APIUtils.formatCost(model.cost)}</div>
                      <div className="text-sm text-gray-600">{APIUtils.formatPercentage(model.performance)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Examples */}
      {!routing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Quick Examples</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-blue-800">Code Generation</h4>
              <p className="text-sm text-blue-700 mt-1">
                Task: Generate React component<br/>
                Complexity: Medium<br/>
                Cost: $0.01-0.05
              </p>
            </div>
            <div>
              <h4 className="font-medium text-blue-800">Code Analysis</h4>
              <p className="text-sm text-blue-700 mt-1">
                Task: Review code quality<br/>
                Complexity: Low<br/>
                Cost: $0.005-0.02
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIRouting;